import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Pencil, Trash2, Plus, ExternalLink } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

type LocationTender = {
  id: string;
  venueName: string;
  venueType?: string;
  slug: string;
  description?: string;
  location?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export default function AdminLocationTenders() {
  const navigate = useNavigate();
  const [locationTenders, setLocationTenders] = useState<LocationTender[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedTender, setSelectedTender] = useState<LocationTender | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadLocationTenders() {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/location-tenders', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLocationTenders(data);
      }
    } catch (error) {
      console.error('Failed to load location tenders:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLocationTenders();
  }, []);

  async function handleDelete() {
    if (!selectedTender) return;

    try {
      const res = await fetch(`/api/admin/location-tenders/${selectedTender.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        await loadLocationTenders();
        setOpenDelete(false);
        setSelectedTender(null);
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to delete location tender');
      }
    } catch (error) {
      console.error('Failed to delete location tender:', error);
      alert('Failed to delete location tender');
    }
  }

  function handleEdit(tender: LocationTender) {
    navigate(`/admin/location-tenders/${tender.id}/edit`);
  }

  function handleViewPublic(tender: LocationTender) {
    window.open(`/newlocation/${tender.slug}`, '_blank');
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading location tenders...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Location Tenders</h1>
        <Button onClick={() => navigate('/admin/location-tenders/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Create New Location Tender
        </Button>
      </div>

      {locationTenders.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No location tenders found.</p>
          <Button onClick={() => navigate('/admin/location-tenders/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Location Tender
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Venue Name</TableHead>
                <TableHead>Venue Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locationTenders.map((tender) => (
                <TableRow key={tender.id}>
                  <TableCell className="font-medium">{tender.venueName}</TableCell>
                  <TableCell>{tender.venueType || '-'}</TableCell>
                  <TableCell>{tender.location || '-'}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">{tender.slug}</code>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        tender.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {tender.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPublic(tender)}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(tender)}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTender(tender);
                          setOpenDelete(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={openDelete} onOpenChange={setOpenDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location Tender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the location tender for "{selectedTender?.venueName}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
